import { Info } from '@drift-labs/icons';
import { ConditionalAsChild } from 'src/components/primitives/ConditionalAsChild';
import { InputSize } from 'src/components/primitives/Input/types';
import { getInputSizeDependentClassName } from 'src/components/primitives/Input/utils';
import { cj, cm } from 'src/utils/classNames';
import { WithClassName, WithChildren } from 'src/utils/UtilityTypes';

interface StackedWrapperProps extends WithClassName, WithChildren {
	disabled?: boolean;
}

/**
 * Use this when you need to stack elements vertically (e.g. label, input, error).
 */
function StackedWrapper({
	disabled,
	className,
	children,
}: StackedWrapperProps) {
	return (
		<div
			className={cm(
				'flex min-w-0 flex-col gap-y-xs',
				disabled && 'cursor-not-allowed',
				className
			)}
		>
			{children}
		</div>
	);
}

interface StandardLabelProps extends WithClassName, WithChildren {
	htmlFor: string | undefined;
	/**
	 * If not passed, renders `children` within an `label`.
	 */
	asChild?: boolean;
}

/**
 * Use this along with `StackedWrapper` when you want an external label above the input.
 */
function StandardLabel({
	htmlFor,
	className,
	children,
	asChild,
}: StandardLabelProps) {
	return (
		<ConditionalAsChild
			asChild={asChild}
			fallback="label"
			className={cm('typo-t4 text-text-secondary', className)}
		>
			<label htmlFor={htmlFor}>{children}</label>
		</ConditionalAsChild>
	);
}

interface ContainerProps extends WithClassName, WithChildren {
	disabled?: boolean;
	error?: boolean;
	/**
	 * If not passed, renders `children` within a `div`.
	 */
	asChild?: boolean;
}

function Container({
	className,
	disabled,
	error,
	children,
	asChild,
}: ContainerProps) {
	return (
		<ConditionalAsChild
			asChild={asChild}
			fallback="div"
			className={cm(
				'flex items-center rounded-sm border border-input-border bg-input-bg transition-colors',
				!disabled && !error && 'hover:border-purple-link',
				disabled && 'border-stroke-divider bg-button-disabled',
				error && 'border-negative-red',
				className
			)}
		>
			{children}
		</ConditionalAsChild>
	);
}

interface InsetLabelProps extends WithClassName, WithChildren {
	disabled?: boolean;
	/**
	 * If not passed, renders `children` within a `div`.
	 */
	asChild?: boolean;
	size?: InputSize;
}

function InsetLabel({
	className,
	disabled,
	asChild,
	children,
	size,
}: InsetLabelProps) {
	const { paddingClassName } = getInputSizeDependentClassName(size);

	return (
		<ConditionalAsChild
			asChild={asChild}
			fallback="div"
			className={cm(
				'flex typo-b4 text-text-secondary transition-colors select-none',
				disabled && 'cursor-not-allowed text-text-disabled',
				paddingClassName,
				className
			)}
		>
			{children}
		</ConditionalAsChild>
	);
}

interface PrefixProps extends WithClassName, WithChildren {
	size?: InputSize;
}

function Prefix({ className, children, size }: PrefixProps) {
	const { paddingClassName } = getInputSizeDependentClassName(size);

	return (
		<div
			className={cj(
				'flex typo-b4 text-text-default select-none',
				paddingClassName,
				className
			)}
		>
			{children}
		</div>
	);
}

interface ValueProps extends WithClassName, WithChildren {
	disabled?: boolean;
	/**
	 * If not passed, renders `children` within an `div`.
	 */
	asChild?: boolean;
	size?: InputSize;
	onClick?: () => void;
}

function Value({
	size,
	asChild,
	disabled,
	className,
	children,
	onClick,
}: ValueProps) {
	const { paddingClassName, textSizeClassName } =
		getInputSizeDependentClassName(size);

	return (
		<ConditionalAsChild
			asChild={asChild}
			fallback="div"
			className={cm(
				'w-full min-w-0 flex-1 border-none bg-transparent transition-colors focus:outline-hidden',
				disabled
					? 'cursor-not-allowed text-text-disabled placeholder:text-text-disabled'
					: 'text-text-default placeholder:text-text-secondary',
				paddingClassName,
				textSizeClassName,
				className
			)}
			onClick={onClick}
		>
			{children}
		</ConditionalAsChild>
	);
}

interface SuffixProps extends WithClassName, WithChildren {
	disabled?: boolean;
	size?: InputSize;
}

function Suffix({ className, disabled, children, size }: SuffixProps) {
	const { paddingClassName, textSizeClassName } =
		getInputSizeDependentClassName(size);

	return (
		<div
			className={cm(
				'flex text-text-default select-none',
				disabled && 'cursor-not-allowed',
				paddingClassName,
				textSizeClassName,
				className
			)}
		>
			{children}
		</div>
	);
}

/**
 * Use this along with `StackedWrapper` when you want to display an error message.
 */
function Error({ className, children }: WithClassName<WithChildren>) {
	return (
		<div
			className={cm(
				'flex items-start gap-x-2xs typo-b4 text-negative-red',
				className
			)}
		>
			<Info size={13} className="mt-px shrink-0" />
			{children}
		</div>
	);
}

export default {
	StackedWrapper,
	StandardLabel,
	Container,
	InsetLabel,
	Prefix,
	Value,
	Suffix,
	Error,
};
